/**
 * Access Requests Page (File Owner View)
 *
 * Security Design:
 * - Shows incoming access requests for the current user's files
 * - Shows outgoing requests made by the current user
 * - Approval requires OTP verification (two-step authorization)
 * - Approval is time-limited (expiry_time)
 * - Even after approval, decryption happens ONLY on the owner's device
 * - No encryption keys or file content are shared
 *
 * Why OTP is required for approval (for viva):
 * - Prevents unauthorized approval if someone hijacks the session
 * - Consistent with the system's two-step verification philosophy
 * - Adds a second authentication factor to the authorization decision
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  getAccessRequests,
  approveAccessRequest,
  rejectAccessRequest,
  requestOTP,
  verifyOTP,
  AUTH_TOKEN_STORAGE_KEY,
  normalizeSessionToken,
} from '../services/api';
import { formatDateTime, formatRelative } from '../utils/time';

/** Read email from session token (JWT payload or legacy dev mock format). Display-only. */
function emailFromStoredToken() {
  try {
    const raw = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    const token = normalizeSessionToken(raw || '');
    if (!token) return '';
    if (token.startsWith('mock-firebase-token-')) {
      const emailPart = token.replace('mock-firebase-token-', '');
      const lastDash = emailPart.lastIndexOf('-');
      return lastDash > 0 ? emailPart.substring(0, lastDash) : emailPart;
    }
    const parts = token.split('.');
    if (parts.length === 3) {
      const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const pad = '='.repeat((4 - (b64.length % 4)) % 4);
      const payload = JSON.parse(atob(b64 + pad));
      if (payload.email) return payload.email;
    }
  } catch {
    /* ignore */
  }
  return '';
}

function AccessRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('incoming'); // 'incoming' or 'outgoing'

  // OTP approval state
  const [approveModal, setApproveModal] = useState(null); // request object
  const [otpCode, setOtpCode] = useState('');
  const [expiryHours, setExpiryHours] = useState(24);
  const [otpSent, setOtpSent] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => { loadRequests(); }, []);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const data = await getAccessRequests();
      setRequests(data || []);
    } catch (err) {
      console.error('[AccessRequests] Failed to load:', err);
    } finally {
      setLoading(false);
    }
  };

  const currentUserEmail = useMemo(() => emailFromStoredToken(), []);

  const incoming = requests.filter(r => r.owner_email === currentUserEmail);
  const outgoing = requests.filter(r => r.requester_email === currentUserEmail);
  const pendingIncoming = incoming.filter(r => r.status === 'PENDING');

  const handleSendOTP = async () => {
    if (!approveModal) return;
    setOtpSending(true);
    try {
      await requestOTP(approveModal.file_id, 'access_approve');
      setOtpSent(true);
      setFeedback({ type: 'success', msg: 'OTP sent to your email. Check your inbox.' });
    } catch (err) {
      setFeedback({ type: 'error', msg: err.message || 'Failed to send OTP' });
    } finally {
      setOtpSending(false);
      setTimeout(() => setFeedback(null), 4000);
    }
  };

  const handleApprove = async () => {
    if (!approveModal || !otpCode || otpCode.length < 6) return;
    setProcessing(true);
    setFeedback(null);
    try {
      await verifyOTP(approveModal.file_id, otpCode, 'access_approve');
      await approveAccessRequest(approveModal.id, expiryHours);
      setFeedback({ type: 'success', msg: `Access approved for ${approveModal.requester_email} (expires in ${expiryHours}h)` });
      setApproveModal(null);
      setOtpCode('');
      setOtpSent(false);
      loadRequests();
      window.dispatchEvent(new Event('accessRequestUpdated'));
    } catch (err) {
      setFeedback({ type: 'error', msg: err.message || 'Failed to approve request' });
    } finally {
      setProcessing(false);
      setTimeout(() => setFeedback(null), 4000);
    }
  };

  const handleReject = async (req) => {
    if (!window.confirm(`Reject access request from ${req.requester_email} for "${req.file_name}"?`)) return;
    setProcessing(true);
    try {
      await rejectAccessRequest(req.id);
      setFeedback({ type: 'success', msg: `Request from ${req.requester_email} rejected.` });
      loadRequests();
      // Notify App.js to refresh badge count immediately
      window.dispatchEvent(new Event('accessRequestUpdated'));
    } catch (err) {
      setFeedback({ type: 'error', msg: err.message || 'Failed to reject request' });
    } finally {
      setProcessing(false);
      setTimeout(() => setFeedback(null), 4000);
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'PENDING': return { bg: 'var(--yellow-muted)', color: 'var(--yellow)' };
      case 'APPROVED': return { bg: 'var(--green-muted)', color: 'var(--green)' };
      case 'REJECTED': return { bg: 'var(--red-muted)', color: 'var(--red)' };
      default: return { bg: 'var(--bg-card)', color: 'var(--text-faint)' };
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(2) + ' MB';
  };

  return (
    <div className="page-container admin-page-shell admin-tactile-page">
      {/* Header */}
      <div style={s.header}>
        <div>
          <h2 style={s.pageTitle}>Access Requests</h2>
          <p style={s.subtitle}>Manage access requests for your encrypted files. Approval requires OTP verification.</p>
        </div>
        <button onClick={loadRequests} className="hover-icon-btn" style={s.refreshBtn} title="Refresh">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
        </button>
      </div>

      {/* Feedback Toast */}
      {feedback && (
        <div style={{ ...s.toast, backgroundColor: feedback.type === 'success' ? 'var(--green)' : 'var(--red)' }}>
          {feedback.msg}
        </div>
      )}

      {/* Tabs */}
      <div style={s.tabs}>
        <button
          style={{ ...s.tab, ...(tab === 'incoming' ? s.tabActive : {}) }}
          onClick={() => setTab('incoming')}
        >
          Incoming ({incoming.length})
          {pendingIncoming.length > 0 && <span style={s.pendingBadge}>{pendingIncoming.length}</span>}
        </button>
        <button
          style={{ ...s.tab, ...(tab === 'outgoing' ? s.tabActive : {}) }}
          onClick={() => setTab('outgoing')}
        >
          My Requests ({outgoing.length})
        </button>
      </div>

      {/* Security Reminder */}
      <div style={s.securityNotice}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
        <span>
          {tab === 'incoming'
            ? 'Approving a request does NOT share your encryption key. You remain the only person who can decrypt the file.'
            : 'Even after approval, file content remains encrypted. Only the file owner can decrypt on their device.'}
        </span>
      </div>

      {/* Request List */}
      {loading ? (
        <div style={s.requestList}>
          {[0,1,2,3].map(i => (
            <div key={i} style={{ background: 'var(--surface-85)', border: '1.5px solid rgba(99,102,241,0.08)', borderRadius: 20, padding: 24, display: 'flex', alignItems: 'center', gap: 16, opacity: 1 - i * 0.15 }}>
              <div style={{ width: 42, height: 42, borderRadius: 14, background: 'var(--border)', opacity: 0.4, animation: 'pulse 1.5s ease-in-out infinite', animationDelay: `${i * 120}ms` }} />
              <div style={{ flex: 1 }}>
                <div style={{ height: 14, width: '50%', borderRadius: 6, background: 'var(--border)', opacity: 0.4, marginBottom: 10 }} />
                <div style={{ height: 10, width: '35%', borderRadius: 4, background: 'var(--border)', opacity: 0.3 }} />
              </div>
              <div style={{ width: 70, height: 28, borderRadius: 10, background: 'var(--border)', opacity: 0.25 }} />
            </div>
          ))}
        </div>
      ) : tab === 'incoming' ? (
        incoming.length === 0 ? (
          <div style={s.emptyState}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--border)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
            </svg>
            <p style={{ color: 'var(--text-secondary)', marginTop: 12, fontSize: 14 }}>No incoming access requests.</p>
            <p style={{ color: 'var(--text-secondary)', fontSize: 12 }}>When others request access to your files, they'll appear here.</p>
          </div>
        ) : (
          <div style={s.requestList}>
            {incoming.map(req => (
              <RequestCard
                key={req.id} req={req} type="incoming"
                getStatusStyle={getStatusStyle} formatSize={formatSize}
                onApprove={() => { setApproveModal(req); setOtpCode(''); setOtpSent(false); }}
                onReject={() => handleReject(req)}
              />
            ))}
          </div>
        )
      ) : (
        outgoing.length === 0 ? (
          <div style={s.emptyState}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--border)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
            <p style={{ color: 'var(--text-secondary)', marginTop: 12, fontSize: 14 }}>You haven't made any access requests.</p>
            <p style={{ color: 'var(--text-secondary)', fontSize: 12 }}>Browse "All Files" to request access to other users' files.</p>
          </div>
        ) : (
          <div style={s.requestList}>
            {outgoing.map(req => (
              <RequestCard
                key={req.id} req={req} type="outgoing"
                getStatusStyle={getStatusStyle} formatSize={formatSize}
              />
            ))}
          </div>
        )
      )}

      {/* OTP Approval Modal */}
      {approveModal && (
        <div style={s.modalOverlay} onClick={() => setApproveModal(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
              Approve Access Request
            </h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
              OTP verification is required to approve this request. Your encryption key will NOT be shared.
            </p>

            {/* Request Details */}
            <div style={s.modalDetail}>
              <div style={s.modalRow}>
                <span style={s.modalLabel}>File</span>
                <span style={s.modalValue}>{approveModal.file_name}</span>
              </div>
              <div style={s.modalRow}>
                <span style={s.modalLabel}>Requester</span>
                <span style={s.modalValue}>{approveModal.requester_email}</span>
              </div>
              {approveModal.reason && (
                <div style={s.modalRow}>
                  <span style={s.modalLabel}>Reason</span>
                  <span style={s.modalValue}>{approveModal.reason}</span>
                </div>
              )}
            </div>

            {/* Access Duration */}
            <div style={{ marginBottom: 16 }}>
              <label style={s.inputLabel}>Access Duration</label>
              <select
                value={expiryHours}
                onChange={e => setExpiryHours(Number(e.target.value))}
                style={s.select}
              >
                <option value={1}>1 hour</option>
                <option value={6}>6 hours</option>
                <option value={12}>12 hours</option>
                <option value={24}>24 hours (default)</option>
                <option value={48}>48 hours</option>
                <option value={168}>7 days</option>
              </select>
            </div>

            {/* OTP Step */}
            <div style={{ marginBottom: 20 }}>
              <label style={s.inputLabel}>OTP Verification</label>
              {!otpSent ? (
                <button onClick={handleSendOTP} disabled={otpSending} className="btn-primary" style={s.otpSendBtn}>
                  {otpSending ? 'Sending OTP...' : 'Send OTP to My Email'}
                </button>
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="text"
                    value={otpCode}
                    onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    onKeyDown={e => { if (e.key === 'Enter' && otpCode.length === 6) handleApprove(); }}
                    placeholder="Enter 6-digit OTP"
                    style={s.otpInput}
                    maxLength={6}
                  />
                  <button onClick={handleSendOTP} disabled={otpSending} style={s.resendBtn} title="Resend OTP">
                    Resend
                  </button>
                </div>
              )}
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setApproveModal(null)} style={s.cancelBtn}>Cancel</button>
              <button
                onClick={handleApprove}
                disabled={processing || !otpCode || otpCode.length < 6}
                className="btn-primary"
                style={{ ...s.approveBtn, opacity: (!otpCode || otpCode.length < 6) ? 0.5 : 1 }}
              >
                {processing ? 'Approving...' : 'Approve Access'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Request Card Component ───────────────────────────────────────
function RequestCard({ req, type, getStatusStyle, formatSize, onApprove, onReject }) {
  const ss = getStatusStyle(req.status);
  const approvedBy =
    req.status === 'APPROVED' && req.approved_by_email
      ? (req.approved_by_role === 'admin'
          ? `Approved by admin: ${req.approved_by_email}`
          : `Approved by owner: ${req.approved_by_email}`)
      : null;

  return (
    <div style={s.reqCard} className="hover-card">
      <div style={s.reqCardHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
          <div style={s.reqIcon}>
            {type === 'incoming' ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--blue)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/>
                <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            )}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {req.file_name}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              {type === 'incoming' ? `From: ${req.requester_email}` : `To: ${req.owner_email}`} · {formatSize(req.file_size)}
            </div>
          </div>
        </div>
        <span style={{ ...s.statusPill, backgroundColor: ss.bg, color: ss.color }}>{req.status}</span>
      </div>

      {req.reason && (
        <div style={s.reasonBox}>
          <span style={{ fontWeight: 500, fontSize: 11, color: 'var(--text-secondary)' }}>Reason:</span>
          <span style={{ fontSize: 13, color: 'var(--text-faint)' }}>{req.reason}</span>
        </div>
      )}

      <div style={s.reqMeta}>
        <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          {formatRelative(req.created_at)} · {formatDateTime(req.created_at)}
        </span>
        {req.expiry_time && (
          <span style={{ fontSize: 11, color: 'var(--green)' }}>
            Expires: {formatDateTime(req.expiry_time)}
          </span>
        )}
      </div>
      {approvedBy && (
        <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600 }}>
          {approvedBy}
        </div>
      )}

      {/* Actions (only for incoming PENDING requests) */}
      {type === 'incoming' && req.status === 'PENDING' && (
        <div style={s.reqActions}>
          <button onClick={onApprove} className="btn-primary" style={s.approveSmBtn}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            Approve
          </button>
          <button onClick={onReject} style={s.rejectSmBtn}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
            Reject
          </button>
        </div>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────
const s = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 16, flexWrap: 'wrap' },
  pageTitle: {
    fontSize: 22, fontWeight: 900, margin: '0 0 6px', letterSpacing: '-0.4px', lineHeight: 1.1,
    background: 'linear-gradient(135deg, #6366F1, #8B5CF6, #06B6D4)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  subtitle: { fontSize: 13, color: 'var(--text-secondary)', margin: 0, fontWeight: 500 },
  refreshBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, borderRadius: 12, border: '1.5px solid rgba(99,102,241,0.12)', backgroundColor: 'var(--surface-85)', cursor: 'pointer', color: 'var(--text-muted)', transition: 'all 0.3s ease', boxShadow: '0 2px 8px rgba(99,102,241,0.06)' },

  tabs: { display: 'flex', gap: 4, marginBottom: 20, borderBottomWidth: 2, borderBottomStyle: 'solid', borderBottomColor: 'rgba(99,102,241,0.06)', paddingBottom: 0 },
  tab: { padding: '12px 24px', fontSize: 13, fontWeight: 700, border: 'none', backgroundColor: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', borderBottomWidth: 3, borderBottomStyle: 'solid', borderBottomColor: 'transparent', marginBottom: -2, display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)', borderRadius: '10px 10px 0 0' },
  tabActive: { color: 'var(--blue)', borderBottomColor: 'var(--blue)', background: 'linear-gradient(180deg, rgba(99,102,241,0.06), transparent)' },
  pendingBadge: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 20, height: 20, borderRadius: 10, background: 'linear-gradient(135deg, #ef4444, #f97316)', color: '#fff', fontSize: 10, fontWeight: 700, padding: '0 5px', boxShadow: '0 2px 8px rgba(239,68,68,0.3)', animation: 'pulse 2s ease-in-out infinite' },

  securityNotice: { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', background: 'linear-gradient(135deg, rgba(16,185,129,0.06), rgba(6,182,212,0.03))', border: '1.5px solid rgba(16,185,129,0.15)', borderRadius: 16, marginBottom: 24, fontSize: 13, color: 'var(--text-faint)', backdropFilter: 'blur(10px)' },

  requestList: { display: 'flex', flexDirection: 'column', gap: 14 },
  reqCard: { background: 'var(--surface-85)', border: '1.5px solid rgba(99,102,241,0.08)', borderRadius: 20, padding: 22, boxShadow: '0 3px 16px rgba(99,102,241,0.06)', backdropFilter: 'blur(12px)', transition: 'all 0.35s cubic-bezier(0.22,1,0.36,1)' },
  reqCardHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10 },
  reqIcon: { width: 42, height: 42, borderRadius: 14, background: 'linear-gradient(135deg, rgba(99,102,241,0.1), rgba(139,92,246,0.06))', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 2px 8px rgba(99,102,241,0.08)' },
  statusPill: { fontSize: 10, fontWeight: 800, padding: '5px 12px', borderRadius: 12, flexShrink: 0, letterSpacing: 0.5, textTransform: 'uppercase' },
  reasonBox: { padding: '10px 14px', background: 'linear-gradient(135deg, rgba(99,102,241,0.03), rgba(139,92,246,0.02))', borderRadius: 12, marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 3, border: '1px solid rgba(99,102,241,0.06)' },
  reqMeta: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 },
  reqActions: { display: 'flex', gap: 10, marginTop: 14, paddingTop: 14, borderTop: '1px solid rgba(99,102,241,0.06)' },
  approveSmBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg, #10B981, #059669)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', boxShadow: '0 3px 10px rgba(16,185,129,0.25)', transition: 'all 0.25s ease' },
  rejectSmBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 12, border: '1.5px solid rgba(239,68,68,0.25)', background: 'linear-gradient(135deg, rgba(239,68,68,0.06), rgba(239,68,68,0.02))', color: 'var(--red)', fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all 0.25s ease' },

  emptyState: { textAlign: 'center', padding: '56px 28px' },

  toast: { position: 'fixed', bottom: 24, right: 24, color: '#fff', padding: '12px 24px', borderRadius: 16, fontSize: 13, fontWeight: 700, boxShadow: '0 8px 32px rgba(0,0,0,0.15)', zIndex: 999, backdropFilter: 'blur(10px)' },

  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15,16,40,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, backdropFilter: 'blur(6px)' },
  modal: { backgroundColor: 'var(--surface-95)', borderRadius: 24, padding: 32, width: 480, maxWidth: '90vw', boxShadow: '0 24px 80px rgba(99,102,241,0.18), 0 8px 24px rgba(0,0,0,0.08)', border: '1.5px solid rgba(99,102,241,0.1)', backdropFilter: 'blur(20px)' },
  modalDetail: { padding: '14px 16px', background: 'linear-gradient(135deg, rgba(99,102,241,0.04), rgba(139,92,246,0.02))', borderRadius: 14, marginBottom: 18, border: '1px solid rgba(99,102,241,0.06)' },
  modalRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0' },
  modalLabel: { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' },
  modalValue: { fontSize: 13, fontWeight: 600, color: 'var(--text-body)', textAlign: 'right', maxWidth: '65%' },
  inputLabel: { fontSize: 12, fontWeight: 700, color: 'var(--text-faint)', marginBottom: 8, display: 'block', textTransform: 'uppercase', letterSpacing: '0.3px' },
  select: { width: '100%', padding: '10px 14px', borderRadius: 12, border: '1.5px solid rgba(99,102,241,0.12)', fontSize: 13, backgroundColor: 'var(--surface-90)', color: 'var(--text-body)', transition: 'all 0.2s ease' },
  otpSendBtn: { width: '100%', padding: '12px 18px', borderRadius: 14, border: 'none', background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 14px rgba(99,102,241,0.3)', transition: 'all 0.25s ease' },
  otpInput: { flex: 1, padding: '12px 16px', borderRadius: 14, border: '1.5px solid rgba(99,102,241,0.15)', fontSize: 18, fontFamily: 'monospace', letterSpacing: 8, textAlign: 'center', backgroundColor: 'var(--surface-90)', color: 'var(--text-primary)', boxShadow: '0 1px 4px rgba(99,102,241,0.04)' },
  resendBtn: { padding: '10px 18px', borderRadius: 12, border: '1.5px solid rgba(99,102,241,0.12)', backgroundColor: 'var(--surface-90)', color: 'var(--text-faint)', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s ease' },
  cancelBtn: { padding: '10px 22px', borderRadius: 14, border: '1.5px solid rgba(99,102,241,0.12)', backgroundColor: 'var(--surface-90)', color: 'var(--text-faint)', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s ease' },
  approveBtn: { padding: '10px 22px', borderRadius: 14, border: 'none', background: 'linear-gradient(135deg, #10B981, #059669)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 14px rgba(16,185,129,0.3)', transition: 'all 0.25s ease' },
};

export default AccessRequestsPage;
